<script setup lang="ts">
import VChart from "vue-echarts";
// Also registers the theme, preprocessor and renderers as a side effect.
import { chartGridInset } from "~/utils/chart";

defineOptions({ name: "Chart" });

interface Props {
  option: Record<string, any>;
  height?: number;
  loading?: boolean;
}

withDefaults(defineProps<Props>(), { height: 280, loading: false });
</script>

<template>
  <div class="chart">
    <!--
      The theme insets the grid so an exported image never has its labels flush
      against the edge. On screen that same inset pushes the chart out of line
      with the card's title, so the canvas is pulled back out by the same amount:
      the axis labels land on the card's padding edge, the exported image keeps
      its margin, and the two stay in sync through one constant.
    -->
    <VChart
      theme="artech"
      :option="option"
      autoresize
      :style="{ height: `${height}px`, marginInline: `-${chartGridInset / 2}px` }"
    />

    <!--
      echarts' own overlay is a bare ring captioned "loading...", painted on an
      opaque white mask that would sit wrong in a dark theme. This is the app's
      spinner over a token-driven scrim instead, so a refetch still shows the
      previous chart underneath rather than a blank panel.
    -->
    <div v-if="loading" class="chart__loading">
      <ProgressCircle size="medium" />
    </div>
  </div>
</template>

<style scoped lang="scss">
.chart {
  position: relative;

  &__loading {
    position: absolute;
    inset: 0;
    z-index: 1;
    display: flex;
    align-items: center;
    justify-content: center;

    // The scrim is its own layer so the spinner stays at full strength on top.
    &::before {
      content: "";
      position: absolute;
      inset: 0;
      background: token("color-bg-surface");
      opacity: 0.72;
    }

    :deep(.progress-circle) {
      position: relative;
    }

    // The stock spin is 800ms, slow enough to look like it has stalled.
    // Matched on the attribute so it beats the layer's own shorthand.
    :deep(.progress-circle[data-state="indeterminate"]) {
      animation-duration: 500ms;
    }
  }
}
</style>
